import { useState } from "react";
import { apiFetch } from "../api";
import { theme } from "../theme";
import { fmt, timeAgo, positionColor, positionBg } from "../utils";
import MiniChart from "./MiniChart";
import TrendBadge from "./TrendBadge";

// ── Product Card ───────────────────────────────────────────────────────────────
export default function ProductCard({ product, onClick, groups, token, onGroupChange, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(product.name);
  const saveName = async () => {
    const newName = editName.trim();
    setEditing(false);
    if (newName === product.name || !newName) return;
    product.name = newName;
    try { await apiFetch("/products/" + product.id, token, { method: "PATCH", body: JSON.stringify({ name: newName }) }); } catch(e) { product.name = editName; }
  };
  const history = product.history || [];
  const isAlert = product.position && history.length >= 2 && history[history.length - 1]?.our_position > history[history.length - 2]?.our_position;

  const capStyle = { color: theme.mut, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 };

  return (
    <div
      onClick={() => onClick(product)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: theme.bg, borderRadius: theme.rCard, padding: "20px 24px",
        border: "1.5px solid " + (isAlert ? theme.badLine : hovered ? "#126DFB40" : theme.line),
        cursor: "pointer", transition: "all 0.2s",
        boxShadow: hovered ? theme.shadowMd : theme.shadowSm,
        transform: hovered ? "translateY(-1px)" : "none",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {editing ? (
            <input
              value={editName}
              onChange={e => setEditName(e.target.value)}
              onBlur={saveName}
              onKeyDown={e => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setEditing(false); setEditName(product.name); } }}
              onClick={e => e.stopPropagation()}
              autoFocus
              style={{ fontSize: 15, fontWeight: 600, color: theme.ink, border: `1.5px solid ${theme.blue}`, borderRadius: 8, padding: "2px 8px", fontFamily: "inherit", width: "100%", background: theme.blueSoft }}
            />
          ) : (
            <p onClick={e => { e.stopPropagation(); setEditing(true); }} style={{ color: theme.ink, fontWeight: 600, fontSize: 15, lineHeight: 1.4, marginBottom: 4, cursor: "text" }} title="Klikni pro přejmenování">
              {product.name} <span style={{ color: "#126DFB80", fontSize: 13, fontWeight: 400, marginLeft: 4 }}>✎</span>
            </p>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <p style={{ color: theme.mut, fontSize: 11, fontVariantNumeric: theme.tnum }}>
              ID: {product.heureka_product_id}
            </p>
            {/* skupina je tu jen k prohlednuti, meni se v detailu produktu */}
            {product.group_name && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 9px", borderRadius: theme.rBtn, background: theme.bgSoft, border: `1px solid ${theme.line}`, fontSize: 11, fontWeight: 600, color: theme.inkSoft }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: product.group_color || theme.blue, flexShrink: 0 }} />
                {product.group_name}
              </span>
            )}
          </div>
          <button onClick={async e => { e.stopPropagation(); if (!window.confirm("Smazat produkt?")) return; try { await apiFetch("/products/" + product.id, token, { method: "DELETE" }); onDelete(product.id); } catch(e) { alert("Chyba: " + (e.detail || e.message || JSON.stringify(e))); } }} style={{ marginTop: 6, background: theme.badSoft, border: `1.5px solid ${theme.badLine}`, borderRadius: theme.rBtn, color: theme.bad, fontSize: 11, fontWeight: 600, cursor: "pointer", padding: "3px 10px", fontFamily: "inherit" }}>
            Odebrat
          </button>
        </div>
        <div style={{
          minWidth: 52, height: 52, borderRadius: theme.rTile,
          background: positionBg(product.position),
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          border: `1.5px solid ${positionColor(product.position)}30`, flexShrink: 0,
        }}>
          <span style={{ color: positionColor(product.position), fontVariantNumeric: theme.tnum, fontWeight: 700, fontSize: 20, lineHeight: 1 }}>
            {product.position ? `${product.position}/${product.total_offers}` : "—"}
          </span>
          <span style={{ color: positionColor(product.position) + "90", fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            pozice
          </span>
        </div>
        <TrendBadge product={product} />
      </div>

      <div style={{ display: "flex", gap: 0, marginTop: 16, borderTop: `1px solid ${theme.line}`, paddingTop: 14, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 120 }}>
          <p style={capStyle}>Naše cena</p>
          <p style={{ color: theme.ink, fontWeight: 700, fontSize: 17, fontVariantNumeric: theme.tnum }}>
            {fmt(product.our_total_price)}
          </p>
          {product.our_delivery_price > 0 && <p style={{ color: theme.mut, fontSize: 11, fontVariantNumeric: theme.tnum }}>{fmt(product.our_price)} + {fmt(product.our_delivery_price)} dop.</p>}
        </div>
        {product.above_shop_name && (
          <div style={{ flex: 1, minWidth: 120, borderLeft: `1px solid ${theme.line}`, paddingLeft: 16 }}>
            <p style={capStyle}>↑ Nad námi</p>
            <p style={{ color: theme.ok, fontWeight: 600, fontSize: 13 }}>{product.above_shop_name}</p>
            <p style={{ color: theme.ok, fontSize: 13, fontVariantNumeric: theme.tnum }}>{fmt(product.above_total_price)}</p>
            {product.above_delivery_price > 0 && <p style={{ color: theme.mut, fontSize: 11, fontVariantNumeric: theme.tnum }}>{fmt(product.above_total_price - product.above_delivery_price)} + {fmt(product.above_delivery_price)} dop.</p>}
          </div>
        )}
        {product.below_shop_name && (
          <div style={{ flex: 1, minWidth: 120, borderLeft: `1px solid ${theme.line}`, paddingLeft: 16 }}>
            <p style={capStyle}>↓ Pod námi</p>
            <p style={{ color: theme.blueDark, fontWeight: 600, fontSize: 13 }}>{product.below_shop_name}</p>
            <p style={{ color: theme.blueDark, fontSize: 13, fontVariantNumeric: theme.tnum }}>{fmt(product.below_total_price)}</p>
            {product.below_delivery_price > 0 && <p style={{ color: theme.mut, fontSize: 11, fontVariantNumeric: theme.tnum }}>{fmt(product.below_total_price - product.below_delivery_price)} + {fmt(product.below_delivery_price)} dop.</p>}
          </div>
        )}
        <div style={{ borderLeft: `1px solid ${theme.line}`, paddingLeft: 16, display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "flex-end" }}>
          <MiniChart history={product.history || []} />
          <p style={{ color: theme.mut, fontSize: 11 }}>{timeAgo(product.last_checked)}</p>
        </div>
      </div>
    </div>
  );
}
